import styled from 'styled-components'
import DropDown from '../generic/DropDown'
import { Icon_Dots } from '../../styles/icons'
import useTranslation from '../../hooks/useTranslation'
import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import useOutsideClick from '../../hooks/useOutsideClick'
import { evaluateCondition } from '../../../../common/model/condition/evaluate'

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.font.color};
  position: relative;
  width: 32px;
  height: 32px;
  
  &:hover {
    background-color: ${({ theme }) => theme.dropdown.active};
  }
  
  svg {
    width: 16px;
    height: 16px;
    margin: 0;
  }
`

// ActionMenu will be portaled into <body>, so we use absolute positioning
const ActionMenu = styled.div`
  position: absolute;
  z-index: 1300;
  margin-top: 0px;
  margin-left: 30px;
`

const ActionContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`

const LineActions = ({ lineActions, item, onAction }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const containerRef = useRef(null)

  /*
   * A menu item's `condition` is tested against the row, so an action only
   * offered for some rows is only shown on those — "Reverse" on a journal that
   * can still be reversed, not on the one it already reversed. A row with
   * nothing left to offer shows no menu at all.
   */
  const menuItems = (lineActions?.menuItems ?? [])
    .filter(menuItem => evaluateCondition(menuItem.condition, { values: item ?? {} }))

  // Every hook runs before the empty-menu return: the row's data — and so
  // whether it has any action left — changes on refetch, and a hook skipped on
  // one render and called on the next is a crash.
  useOutsideClick(containerRef, () => setIsOpen(false), 'normal')

  if (menuItems.length === 0) {
    return null
  }

  const handleToggleClick = (e) => {
    e.preventDefault()
    e.stopPropagation()

    const rect = e.currentTarget.getBoundingClientRect()
    setMenuPosition({
      top: rect.bottom + window.scrollY, // position below the button
      left: rect.left + window.scrollX,  // align left with button
    })

    setIsOpen(prev => !prev)
  }

  const handleActionClick = (e, menuItem) => {
    e.preventDefault()
    e.stopPropagation()

    if (onAction) {
      onAction(menuItem, item)
    }
    setIsOpen(false)
  }

  return (
    <ActionContainer ref={containerRef}>
      <DropDown>
        <ActionButton
          type="button"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={handleToggleClick}
          $isOpen={isOpen}
        >
          {Icon_Dots}
        </ActionButton>
        {isOpen &&
          createPortal(
            <ActionMenu style={{ top: menuPosition.top, left: menuPosition.left }}>
              <DropDown.Menu role="menu">
                {menuItems.map((menuItem, index) => (
                  <DropDown.Item
                    key={index}
                    href="#"
                    onClick={(e) => handleActionClick(e, menuItem)}
                  >
                    {t(menuItem.label)}
                  </DropDown.Item>
                ))}
              </DropDown.Menu>
            </ActionMenu>,
            document.body
          )}
      </DropDown>
    </ActionContainer>
  )
}

export default LineActions
